import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Select,
  Switch,
  Text,
} from '@chakra-ui/react'
import CustomTooltip from 'brokertslib/src/components/CustomTooltip'
import type { Offer, OfferLeasing, PackageItemModel } from 'BrokerTsLib/src/dtos'
import { PackageItemLimitType } from 'BrokerTsLib/src/dtos'
import { useEffect } from 'react'

import { useTranslationsContext } from 'BrokerTsLib/src/context/TranslationsContext'
import { Risk } from 'BrokerTsLib/src/enums'
import { useInsuranceParams } from '../../context/InsuranceParamsProvider'
import { useGetOfferAvailablePackageItems } from '../../hooks/useOfferPackage'
import { Loading } from '../Loading'
import { QuestionMarkIcon } from '../QuestionMarkIcon'

export const QuoteItemPackageSettingsForm = ({
  offer,
  addons,
  setAddons,
}: {
  offer: OfferLeasing | Offer
  addons: any
  setAddons: React.Dispatch<any>
}) => {
  const { translate } = useTranslationsContext()
  const {
    data,
    isLoading,
    refetch: refetchAvailablePackageItems,
  } = useGetOfferAvailablePackageItems(offer.offerId)
  const { isDisabled } = useInsuranceParams()

  const onSelectAddon = (
    item: PackageItemModel,
    packageItemVariantId: number | undefined,
    included: boolean,
  ) => {
    setAddons((x: any) => ({
      ...x,
      [item.riskId]: { packageItem: item, riskId: item.riskId, packageItemVariantId, included },
    }))
  }

  const onRemoveAddon = (item: PackageItemModel) => {
    setAddons((x: any) => ({
      ...x,
      [item.riskId]: { packageItem: item, riskId: item.riskId, packageItemVariantId: undefined, included: false },
    }))
  }

  useEffect(() => {
    refetchAvailablePackageItems()
  }, [offer.dateCalculated])

  const availableItems = data?.data
    .map(x => x as PackageItemModel)
    .filter(x => ![Risk.POV, Risk.HAV, Risk.SPOL].includes(x.riskId)) ?? []

  return (
    <>
      {availableItems.length === 0 && isLoading && <Loading large={true} />}

      <Grid
        sx={{
          gridTemplateColumns: { lg: 'repeat(2, 1fr)' },
          columnGap: 8,
          rowGap: 8,
          alignItems: 'start',
        }}
      >
        {availableItems.length === 0 && !isLoading && (
          <Text>{translate('quoteItem.settingsForm.noAddons', 'Žádné připojištění není k dispozici')}</Text>
        )}

        {availableItems.length > 0 &&
          availableItems.map((item) => {
            const currentAddon = addons[item.riskId]
            const isIncludedAddon = currentAddon !== undefined ? currentAddon.included : item.included
            const currentVariantId = currentAddon?.packageItemVariantId
            const isBoolean = item.limitType === PackageItemLimitType.Boolean || item.variants.length <= 1

            if (isBoolean) {
              const variant = item.variants[0]
              return (
                <FormControl sx={{ gap: 4 }} key={item.riskId}>
                  <GroupLabel
                    label={item.riskName}
                    tooltip={
                      item.description ? (
                        <Box dangerouslySetInnerHTML={{ __html: item.description }} />
                      ) : undefined
                    }
                  />
                  <FormControl display="flex" alignItems="center">
                    <Switch
                      colorScheme="green"
                      size="lg"
                      dir="rtl"
                      isDisabled={isDisabled}
                      isChecked={currentVariantId == variant?.id}
                      onChange={(e) => {
                        if (e.target.checked)
                          onSelectAddon(item, variant?.id, true)
                        else
                          onRemoveAddon(item)
                      }}
                    />
                  </FormControl>
                </FormControl>
              )
            }

            const dropdownDs = [
              {
                id: 0,
                variantId: undefined as number | undefined,
                label: translate('withoutAdditionalInsurance', 'Bez připojištění'),
              },
              ...item.variants.map((v) => ({
                id: v.id,
                variantId: v.id,
                label: v.insurerProductName ?? v.limitText ?? String(v.defaultLimit ?? v.id),
              })),
            ]

            return (
              <FormControl sx={{ gap: 4 }} key={item.riskId}>
                <GroupLabel
                  label={item.riskName}
                  tooltip={
                    item.description ? (
                      <Text dangerouslySetInnerHTML={{ __html: item.description }} />
                    ) : undefined
                  }
                />
                <FormControl
                  display="flex"
                  flexDirection="column"
                  alignItems="start"
                  width="100%"
                >
                  <Select
                    sx={{ maxWidth: { md: '295px' } }}
                    variant="outline"
                    isDisabled={isDisabled}
                    value={isIncludedAddon ? (currentVariantId?.toString() ?? '') : ''}
                    onChange={(e) => {
                      const variantId = e.target.value ? parseInt(e.target.value) : undefined
                      if (!variantId) {
                        onRemoveAddon(item)
                      } else {
                        onSelectAddon(item, variantId, true)
                      }
                    }}
                  >
                    {dropdownDs.map((x) => (
                      <option key={x.id} value={x.variantId?.toString() ?? ''}>
                        {x.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </FormControl>
            )
          })}
      </Grid>
    </>
  )
}

const GroupLabel = ({
  label,
  tooltip,
}: {
  label: string
  tooltip?: string | React.ReactNode
}) => (
  <Flex sx={{ w: '100%', justifyContent: 'space-between' }}>
    <FormLabel>{label}</FormLabel>

    {tooltip && (
      <CustomTooltip
        trigger="clickHover"
        maxW="500px"
        hasArrow
        label={tooltip}
        placement="auto"
      >
        <Box sx={{ lineHeight: 0 }}>
          <QuestionMarkIcon ml={2} />
        </Box>
      </CustomTooltip>
    )}
  </Flex>
)
